/**
 * Horário fixo — "o João vem toda terça às 17h".
 *
 * Só a equipe cria e mexe: o cliente vê cada data em "Meus horários", com a
 * marca de horário fixo, e cancela uma delas pelas regras de sempre.
 *
 * A série guarda **quais** serviços, nunca quanto tempo: cada data gerada leva o
 * tempo que a ficha do cliente manda naquele dia, que é como o resto do produto
 * funciona. Quem quer outro tempo corrige na ficha — e a correção vale também
 * para o dia em que ele vier fora do horário fixo.
 */

import { z } from 'zod';
import { MAX_SERVICES_PER_APPOINTMENT } from './catalog.js';
import { calendarDateSchema, idSchema, instantSchema } from './common.js';
import { weekdaySchema } from './schedule.js';
import { describeWeekdays } from './waitlist.js';

/** Igual ao `MAX_INTERVAL_WEEKS` do core; repetido para o app não importar o core. */
export const MAX_SERIES_INTERVAL_WEEKS = 8;

/** As opções que a tela oferece. Dois meses é o teto: depois disso é lembrete, não horário fixo. */
export const SERIES_INTERVAL_OPTIONS = [1, 2, 3, 4, 6, 8] as const;

const seriesRuleShape = {
  /** Na ordem em que o barbeiro vai fazer; o primeiro é o principal. */
  serviceIds: z
    .array(idSchema)
    .min(1, 'Escolha ao menos um serviço')
    .max(MAX_SERVICES_PER_APPOINTMENT, 'Serviços demais para um atendimento só')
    .refine((ids) => new Set(ids).size === ids.length, 'O mesmo serviço aparece duas vezes'),
  weekdays: z
    .array(weekdaySchema)
    .min(1, 'Escolha ao menos um dia da semana')
    .refine((days) => new Set(days).size === days.length, 'O mesmo dia aparece duas vezes'),
  /** Minuto local do início: 17h = 1020. */
  startMinute: z.number().int().min(0).max(1439, 'Horário fora do dia'),
  intervalWeeks: z
    .number()
    .int()
    .min(1, 'O intervalo mínimo é toda semana')
    .max(MAX_SERIES_INTERVAL_WEEKS, `O intervalo máximo é de ${MAX_SERIES_INTERVAL_WEEKS} semanas`),
  /** A primeira data. É também a âncora do "semana sim, semana não". */
  startDate: calendarDateSchema,
  /** Nulo ou ausente = sem fim. */
  endDate: calendarDateSchema.nullish(),
  membershipId: idSchema.optional(),
  notes: z.string().trim().max(500).optional(),
};

function endAfterStart(rule: { startDate: string; endDate?: string | null }): boolean {
  return !rule.endDate || rule.endDate >= rule.startDate;
}

export const createSeriesSchema = z
  .object({
    clientId: idSchema,
    ...seriesRuleShape,
    /**
     * O atendimento a partir do qual o barbeiro tocou "Repetir". Se ele cair
     * numa data e hora da regra, vira a primeira data da série em vez de a
     * geração marcar outro em cima dele.
     */
    fromAppointmentId: idSchema.optional(),
  })
  .refine(endAfterStart, { message: 'O fim vem antes do começo', path: ['endDate'] });

/**
 * O que fazer com as datas que já estão marcadas quando a regra muda.
 *
 *  - `ALL_FUTURE`: elas acompanham — a terça vira quarta, as semanas que saíram
 *    são canceladas. As que o barbeiro alterou à mão ficam como estão.
 *  - `ONLY_NEW`: elas ficam; a regra nova começa depois da última já marcada.
 *
 * Não tem padrão de propósito: é a pergunta que a tela faz na hora de salvar, e
 * um padrão silencioso aqui decidiria por ele.
 */
export const seriesChangeScopeSchema = z.enum(['ALL_FUTURE', 'ONLY_NEW']);
export type SeriesChangeScope = z.infer<typeof seriesChangeScopeSchema>;

export const updateSeriesSchema = z
  .object({ ...seriesRuleShape, scope: seriesChangeScopeSchema })
  .refine(endAfterStart, { message: 'O fim vem antes do começo', path: ['endDate'] });

export const endSeriesSchema = z.object({
  /**
   * Cancelar também as datas já marcadas. Sem isto elas continuam na agenda e a
   * série só para de gerar. Também é pergunta da tela, sem padrão.
   */
  cancelFuture: z.boolean(),
});

/** Marcar uma data pulada: no horário da regra ou em outro, encaixando se ele quiser. */
export const bookSkippedSchema = z.object({
  startsAt: instantSchema,
  acceptConflict: z.boolean().default(false),
});

export const listSeriesSchema = z.object({
  clientId: idSchema.optional(),
});

// ── Leitura ─────────────────────────────────────────────────────────────────

export const SKIP_REASONS = ['TIME_OFF', 'OUTSIDE_HOURS', 'OVERLAP'] as const;
export type SeriesSkipReason = (typeof SKIP_REASONS)[number];

export const SKIP_REASON_LABELS: Record<SeriesSkipReason, string> = {
  TIME_OFF: 'folga ou feriado',
  OUTSIDE_HOURS: 'fora do expediente',
  OVERLAP: 'já tem cliente nesse horário',
};

/** Uma data da regra que não virou atendimento. */
export interface SeriesSkipView {
  seriesId: string;
  /** A data da regra, `AAAA-MM-DD`. */
  date: string;
  /** Onde ela teria caído. */
  startsAt: string;
  endsAt: string;
  /**
   * Por que ela não virou atendimento, calculado agora. Nulo = o motivo sumiu
   * (a folga foi retirada, o outro cliente desmarcou) e a data já caberia.
   */
  reason: SeriesSkipReason | null;
  overlapMinutes: number;
  client: { id: string; name: string };
  /** "Toda terça às 17h" — para a lista de pulos fazer sentido sem abrir a série. */
  label: string;
}

/** Uma data já marcada da série. */
export interface SeriesOccurrenceView {
  appointmentId: string;
  /** A data da regra que ela ocupa. */
  date: string;
  startsAt: string;
  status: string;
  /** O barbeiro mexeu nela à mão: mudar a regra não passa por cima. */
  detached: boolean;
}

export interface SeriesView {
  id: string;
  client: { id: string; name: string };
  staff: { id: string; name: string };
  services: Array<{ id: string; name: string; color: string }>;
  weekdays: number[];
  startMinute: number;
  intervalWeeks: number;
  startDate: string;
  endDate: string | null;
  generatedUntil: string | null;
  endedAt: string | null;
  notes: string | null;
  label: string;
  /** Próximas datas marcadas, da mais próxima para a mais distante. */
  upcoming: SeriesOccurrenceView[];
  skips: SeriesSkipView[];
}

/** O que uma geração fez — a tela diz isso em uma frase depois de salvar. */
export interface SeriesOutcomeView {
  series: SeriesView;
  created: number;
  moved: number;
  canceled: number;
  skipped: SeriesSkipView[];
}

/** Como o atendimento diz que veio de um horário fixo. */
export interface AppointmentSeriesView {
  id: string;
  label: string;
}

// ── Textos ──────────────────────────────────────────────────────────────────

function hour(minute: number): string {
  const hours = Math.floor(minute / 60);
  const rest = minute % 60;
  return rest === 0 ? `${hours}h` : `${hours}h${String(rest).padStart(2, '0')}`;
}

/** "toda" ou "todo", conforme o dia. Sábado e domingo são masculinos. */
function every(weekday: number): string {
  return weekday === 0 || weekday === 6 ? 'Todo' : 'Toda';
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * A regra em uma frase, do jeito que o barbeiro fala no balcão.
 *
 *   "Toda terça às 17h"
 *   "Quarta sim, quarta não, às 17h"
 *   "Terça e sexta, toda semana, às 17h"
 *   "A cada 4 semanas, na sexta, às 9h30"
 */
export function describeRecurrence(rule: {
  weekdays: readonly number[];
  startMinute: number;
  intervalWeeks: number;
}): string {
  const days = describeWeekdays(rule.weekdays);
  const time = hour(rule.startMinute);
  const single = rule.weekdays.length === 1 ? (rule.weekdays[0] as number) : null;

  if (rule.intervalWeeks === 1) {
    return single !== null
      ? `${every(single)} ${days} às ${time}`
      : `${capitalize(days)}, toda semana, às ${time}`;
  }

  if (rule.intervalWeeks === 2) {
    return single !== null
      ? `${capitalize(days)} sim, ${days} não, às ${time}`
      : `${capitalize(days)}, semana sim, semana não, às ${time}`;
  }

  const article = single === 0 || single === 6 ? 'no' : 'na';
  return single !== null
    ? `A cada ${rule.intervalWeeks} semanas, ${article} ${days}, às ${time}`
    : `A cada ${rule.intervalWeeks} semanas, ${days}, às ${time}`;
}

/**
 * O resultado de salvar, em uma frase: "8 datas marcadas. 1 ficou de fora."
 *
 * A tela mostra os pulos logo abaixo, um por um; a frase só dá a conta.
 */
export function describeSeriesOutcome(outcome: {
  created: number;
  moved: number;
  canceled: number;
  skipped: readonly unknown[];
}): string {
  const parts: string[] = [];
  const plural = (count: number, one: string, many: string) =>
    `${count} ${count === 1 ? one : many}`;

  if (outcome.created > 0) parts.push(plural(outcome.created, 'data marcada', 'datas marcadas'));
  if (outcome.moved > 0) parts.push(plural(outcome.moved, 'remarcada', 'remarcadas'));
  if (outcome.canceled > 0) parts.push(plural(outcome.canceled, 'cancelada', 'canceladas'));
  if (outcome.skipped.length > 0) {
    parts.push(plural(outcome.skipped.length, 'ficou de fora', 'ficaram de fora'));
  }

  return parts.length > 0 ? `${parts.join(', ')}.` : 'Nada mudou na agenda.';
}
